import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';

import Button from '@atlaskit/button/new';
import { GoogleCalendarIconBorderless } from '@atlassian/conversation-assistant-chat-prompt-input/3p-product-configs/google-calendar/icon-borderless';
import { SlackIconBorderless } from '@atlassian/conversation-assistant-chat-prompt-input/3p-product-configs/slack/icon-borderless';
import { useOAuthHandlerFactory } from '@atlassian/conversation-assistant-chat-prompt-input/useOAuthHandlerFactory';
import {
	type ConnectorState,
	useEnabledKnowledgeSources,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';

export type ConnectAppProduct = 'slack' | 'google-calendar';

type MessageKeys = 'connectSlack' | 'connectGoogleCalendar';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		connectSlack: {
			id: 'rovo-settings-modal.connect-app-button.ConnectAppButton.connectSlack',
			defaultMessage: 'Connect Slack',
			description:
				'Label for the button that starts connecting the user’s Slack account to Rovo insights.',
		},
		connectGoogleCalendar: {
			id: 'rovo-settings-modal.connect-app-button.ConnectAppButton.connectGoogleCalendar',
			defaultMessage: 'Connect Google Cal',
			description:
				'Label for the button that starts connecting the user’s Google Calendar account to Rovo insights.',
		},
	});

type ConnectAppProductConfig = {
	label: MessageDescriptor;
	resourceTypes: readonly string[];
	sources: readonly string[];
	testId: string;
	/**
	 * Bundled, borderless (un-tiled) brand glyph. We intentionally use the
	 * `*IconBorderless` components rather than `@atlassian/logo-third-party`,
	 * which always wraps its logos in an ADS `<Tile>` — the button design calls
	 * for a bare brand mark with no tile.
	 */
	Icon: () => React.JSX.Element;
};

const PRODUCT_CONFIGS: Record<ConnectAppProduct, ConnectAppProductConfig> = {
	slack: {
		label: messages.connectSlack,
		resourceTypes: ['slack-connector'],
		sources: ['slack'],
		testId: 'insights-connect-slack-button',
		Icon: SlackIconBorderless,
	},
	'google-calendar': {
		label: messages.connectGoogleCalendar,
		resourceTypes: ['google-calendar-connector'],
		sources: ['google-calendar', 'google_calendar'],
		testId: 'insights-connect-google-calendar-button',
		Icon: GoogleCalendarIconBorderless,
	},
};

export type ConnectAppButtonProps = {
	product: ConnectAppProduct;
	isCompact?: boolean;
	analyticsSource: string;
	analyticsSurface?: string;
	onVisibilityChange?: (isVisible: boolean) => void;
};

const matchesProduct = (
	connector: ConnectorState,
	{ resourceTypes, sources }: ConnectAppProductConfig,
): boolean =>
	resourceTypes.includes(connector.resourceType ?? '') || sources.includes(connector.source);

export const ConnectAppButton = ({
	product,
	isCompact = false,
	analyticsSource,
	analyticsSurface,
	onVisibilityChange,
}: ConnectAppButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [knowledgeSources] = useEnabledKnowledgeSources();
	const createOAuthHandler = useOAuthHandlerFactory();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: analyticsSource,
	});
	// Tracks the product the user just connected via OAuth, so we can optimistically hide its button.
	// Keying by product (instead of a boolean + reset effect) keeps the state correct when `product`
	// changes without an extra post-render reset.
	const [locallyConnectedProduct, setLocallyConnectedProduct] = useState<ConnectAppProduct | null>(
		null,
	);
	const productConfig = PRODUCT_CONFIGS[product];

	useEnsureConnectorsLoaded({ assistanceServiceParams });

	const connector = useMemo(
		() =>
			knowledgeSources.find(
				(source) =>
					matchesProduct(source, productConfig) &&
					!source.isUserOAuthed &&
					Boolean(source.outboundAuthUrl) &&
					locallyConnectedProduct !== product,
			),
		[locallyConnectedProduct, product, knowledgeSources, productConfig],
	);

	useEffect(() => {
		onVisibilityChange?.(Boolean(connector));
	}, [connector, onVisibilityChange]);

	const handleConnect = useCallback(() => {
		if (!connector) {
			return;
		}

		const { onAuthorize } = createOAuthHandler({
			friendlyName: connector.friendlyName,
			appSource: connector.source,
			outboundAuthUrl: connector.outboundAuthUrl,
			onCompletedAction: () => {
				setLocallyConnectedProduct(product);
			},
			analyticsAttributes: {
				source: analyticsSource,
				surface: analyticsSurface,
			},
		});

		void onAuthorize();
	}, [analyticsSource, analyticsSurface, connector, createOAuthHandler, product]);

	if (!connector) {
		return null;
	}

	const { Icon } = productConfig;

	return (
		<Button
			appearance={isCompact ? 'default' : 'subtle'}
			spacing="default"
			iconBefore={Icon}
			onClick={handleConnect}
			testId={productConfig.testId}
		>
			{formatMessage(productConfig.label)}
		</Button>
	);
};
